import { apiEmployeeEmployment } from "@/api/employees/employee-employment.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { employeeEmploymentHistoryKeys } from "@/queries/employees/use-employee-employment-history.query.ts";
import { employeeCustomTableKeys } from "@/queries/employees/use-employee-custom-table.query.ts";
import { employeesKeys } from "@/queries/employees/use-employees.query.ts";
import { ApiErrorException } from "@/types/api.types.ts";
import { ApiEmployeeEmploymentPayload } from "@/types/employees/employee-employment.types.ts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

export interface SaveParams {
  employeeId: number;
  employmentId?: number;
  payload: ApiEmployeeEmploymentPayload;
}

export const useEmployeeEmploymentSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({ employeeId, employmentId, payload }: SaveParams) => {
      const result = !employmentId
        ? await apiEmployeeEmployment.create(employeeId, payload)
        : await apiEmployeeEmployment.update(employeeId, employmentId, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: employeesKeys.detail(variables.employeeId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: employeeCustomTableKeys.search(variables.employeeId, 0, {}).queryKey.slice(0, 3),
      });
      queryClient.invalidateQueries({
        queryKey: employeeEmploymentHistoryKeys.all(variables.employeeId).queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useEmployeeEmploymentDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ employeeId, positionId }: { employeeId: number; positionId: number }) => {
      const result = await apiEmployeeEmployment.delete(employeeId, positionId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: employeesKeys.detail(variables.employeeId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: employeeCustomTableKeys.search(variables.employeeId, 0, {}).queryKey.slice(0, 3),
      });
      queryClient.invalidateQueries({
        queryKey: employeeEmploymentHistoryKeys.all(variables.employeeId).queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
